import React, {useEffect} from "react";
import MakeTabs from "./Components/Make/Tabs";
import {useAppDispatch, useAppSelector} from "./store/hooks";
import {BrowserRouter} from "react-router-dom";
import {YMInitializer} from "react-yandex-metrika";
import getMakerData from "./Api/getMakerData";
import {useMutation} from "@tanstack/react-query";
import axios from "axios";
import * as Sentry from "@sentry/react";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {SWAL_ALERT_DEFAULTS} from "./Utils/defaults";
import bridge from "@vkontakte/vk-bridge";
import {v4 as uuidv4} from "uuid";
import {
    setAllowCheckPaymentDone,
    setIsMusicPlaying,
    setIsPreviewPlaying,
    setVkClientVersion,
    setVkLaunchParams
} from "./store/makerDataSlice";
import getPreviewStatus, {canGetPreviewStatus} from "./Api/getPreviewStatus";

interface MakeAppProps {
}

function MakeApp(props: MakeAppProps) {

    const dispatch = useAppDispatch();
    const makerData = useAppSelector(state => state.makerData)

    const mutation = useMutation({
        mutationFn: getMakerData,
        onError: (error, variables, context) => {
            let errorUrl = '';
            if (axios.isAxiosError(error)) {
                errorUrl = String(error.config?.url);
                // @ts-ignore
            } else if (typeof error?.url !== 'undefined') {
                // @ts-ignore
                errorUrl = error?.url;
            }
            Sentry.captureException(
                // @ts-ignore
                new Error((error?.message ? error.message : 'Failed to load maker data. Generic error.') + ', URL: ' + errorUrl),
                {
                    extra: {
                        variables: variables,
                        error: error,
                        responseConfig: axios.isAxiosError(error) ? JSON.stringify(error.response?.config) : '',
                        responseData: axios.isAxiosError(error) ? JSON.stringify(error.response?.data) : ''
                    }
                }
            )
            const MySwal = withReactContent(Swal);
            MySwal.fire({
                ...SWAL_ALERT_DEFAULTS,
                title: 'Ошибка',
                text: 'Ошибка загрузки данных. Попробуйте обновить страницу.',
                icon: 'error'
            });
        },
        retry: 5,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000)
    })

    const doGetMakerData = () => {
        mutation.mutateAsync({
            params: {
                savedProjectId: makerData?.savedProjectData?.id,
                savedProjectUid: makerData?.savedProjectData?.uid,
                savedProjectVp: makerData?.savedProjectData?.vp,
            },
            dispatch: dispatch,
            makerData: makerData
        });
        if (typeof makerData?.userId !== 'undefined' && makerData?.userId != null) {
            Sentry.setUser({id: makerData.userId})
        }
    }

    useEffect(() => {
        // Если запускается ВНЕ VK - сразу получаем данные MakerData
        if (bridge.isStandalone()) {
            doGetMakerData();
        } else {
            // Если запускается внутри VK И НЕ ПЕРЕДАНЫ GET параметры
            // (пример - переход из платёжной формы Тинькофф, по кнопке "Вернуться в магазин",
            // которая ведёт на slide-life.ru/vk/ БЕЗ GET параметров от VK )
            // то делаем redirect на страницу приложения C GET параметрами
            // т.к. store (сессия пользователя) для VK загружается с сервера
            // и если нет GET параметров - то мы не получим пользовательскую сессию
            // там (в store.ts) вызвать bridge.send('VKWebAppGetLaunchParams') не получится, т.к.
            // нужно синхронно получить данные по сессии
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has('vk_app_id')) {
                if (urlParams.has('vk_client') && urlParams.get('vk_client') == 'ok') {
                    // если OK
                    // сохраняем URL Params в localstorage, чтобы после оплаты по кнопке "вернуться в магазин"
                    // взять эти параметры и сделать правильный редирект на страницу приложения с параметрами
                    localStorage.setItem('sl-ok-launch-params', JSON.stringify(Object.fromEntries(urlParams)));
                }

                // Если ОК приложение - просто получаем данные, там не работает
                // bridge.send('VKWebAppGetLaunchParams')
                if (urlParams.has('vk_client') && urlParams.get('vk_client') == 'ok'
                    && (urlParams.get('vk_platform') == 'mobile_android' || urlParams.get('vk_platform') == 'mobile_ipad' || urlParams.get('vk_platform') == 'mobile_iphone')) {
                    doGetMakerData();
                } else {
                    // если есть GET параметр vk_app_id - всё нормально, получаем данные и делаем
                    // bridge.send('VKWebAppGetClientVersion')
                    dispatch(setVkLaunchParams(null));
                    bridge.send('VKWebAppGetLaunchParams')
                        .then((data) => {
                            if (data.vk_app_id) {
                                dispatch(setVkLaunchParams(data));
                            }
                        })
                        .catch((error) => {
                            // Если произошла ошибка при вызове - сохраняем URL params в launchParams
                            dispatch(setVkLaunchParams(Object.fromEntries(urlParams)));
                        })
                        .finally(() => {
                            doGetMakerData();
                        });
                }

                dispatch(setVkClientVersion(null));
                bridge.send('VKWebAppGetClientVersion')
                    .then((data) => {
                        if (data.platform) {
                            dispatch(setVkClientVersion(data.platform));
                        }
                    })
                    .catch((error) => {
                        Sentry.captureException(error);
                    });

                bridge.subscribe((e) => {
                    if (e.detail.type === 'VKWebAppViewHide') {
                        // останавливаем мелодию, играющее preview если пользователь закрывает mini-App
                        dispatch(setIsMusicPlaying(false));
                        dispatch(setIsPreviewPlaying(false));
                        dispatch(setAllowCheckPaymentDone(false));
                        canGetPreviewStatus.value = false;
                    }
                });

                bridge.subscribe((e) => {
                    if (e.detail.type === 'VKWebAppChangeFragment') {
                        // destroy'им все MySwal при нажатии на системную кнопку "назад" в android'е или в браузере
                        const MySwal = withReactContent(Swal);
                        MySwal.close();
                    }
                });

                if (urlParams.has('vk_client') && urlParams.get('vk_client') == 'ok') {
                    // если OK - не вызываем VKWebAppSetViewSettings, там ошибка возникает
                    // do nothing
                } else {
                    // устанавливаем цвет статус-бара на светлый, пока нет темной темы
                    bridge.send('VKWebAppSetViewSettings', {
                        status_bar_style: 'dark',
                        action_bar_color: '#ffffff'
                    })
                        .then((data) => {
                            // forgive
                        })
                        .catch((error) => {
                            // forgive
                        });
                }
            } else {
                dispatch(setVkLaunchParams(null));
                if (urlParams.has('vk_client') && urlParams.get('vk_client') == 'ok') {
                    // если OK - не вызываем VKWebAppGetLaunchParams
                    // пробуем получить launchParams из localStorage
                    if (localStorage.getItem('sl-ok-launch-params') === null) {
                        window.location.href = '/vk/app?' + (new URLSearchParams(JSON.parse(localStorage.getItem('sl-ok-launch-params') ?? '')).toString());
                    }
                } else {
                    // если нет GET параметра vk_app_id
                    // получаем данные из bridge.send('VKWebAppGetClientVersion')
                    // и делаем redirect
                    bridge.send('VKWebAppGetLaunchParams')
                        .then((data) => {
                            // @ts-ignore
                            window.location.href = '/vk/app?' + (new URLSearchParams(data).toString())
                        })
                        .catch((error) => {
                            Sentry.captureException(error);
                        });
                }
            }
        }
    }, []);

    return (
        <div className={'make-app'}>
            <BrowserRouter>
                <MakeTabs/>
            </BrowserRouter>
            <YMInitializer accounts={typeof (window as any).slYandexMetrikaCounter !== 'undefined' ? [(window as any).slYandexMetrikaCounter] :  [29900459]}/>
        </div>
    );
}

export default MakeApp;